/**
 * Product gallery component
 * 
 * @module product-gallery-component
 * @version 1.0.0
 * @extends HTMLElement
 */
class productGalleryComponent extends HTMLElement {

    /**
     * Product gallery web component.
     * 
     * @constructor
     */
    constructor() {

        super();

        this.initEvents();

        this.bindNavigation();
    }

    /**
     * Bind single-click navigation to the PDP.
     *
     * Routed through the product card name link (already present on load)
     * so the existing productClick dataLayer tracking fires. Fixes the
     * multi-image card double-click: the PDP link used to be injected
     * lazily by the hover gallery, so the first click on the image had no
     * link to follow.
     *
     * @returns {undefined}
     */
    bindNavigation() {

        this.addEventListener('click', (event) => {

            if (event.target.closest('.swiper-button-next, .swiper-button-prev, .swiper-pagination')) {
                return;
            }

            const card = this.closest('product-card');
            const nameLink = card && card.querySelector('a.product-card__name');

            if (nameLink) {
                event.preventDefault();
                nameLink.click();
            } else if (this.dataset.handle) {
                event.preventDefault();
                window.location.href = '/products/' + this.dataset.handle;
            }
        });
    }



    /**
     * Init events.
     * 
     * @returns {undefined}
     */
    initEvents() {

        if (window.isTouch) {

            if (this.classList.contains('active')) {
                this.destroy();
            } else {
                this.load(); 
            }

        } else {

            hoverintent(this, () => {

                this.classList.add('hover');
                
                this.load();
    
            }, () => {
    
                this.classList.remove('hover');
    
                this.destroy();
            });
        }
    }

    /**
     * Load component.
     * 
     * @returns {undefined}
     */
    load() {

        if (!this.classList.contains('active')) {

            this.classList.add('active');

            if (this.dataset.media) {
                let media = JSON.parse(this.dataset.media);
                if (media.length > 1) {
                    this.renderFromMedia(media);
                }
                return;
            }

            if (this.dataset.preRendered === 'true' && this.querySelector('.product-gallery__images')) {
                let element = this.querySelector('.product-gallery__images');
                element.classList.add('active');
                this.classList.add('slide-active');
                if (!this.slider) {
                    this.initSlider();
                }
                return;
            }

            this.classList.add('loading-gallery');

            if (window.theme && window.theme.product && typeof window.theme.product.loadProduct === 'function') {
                window.theme.product.loadProduct(this.dataset.handle).then(product => {
                    // loadProduct resolves null on a failed or throttled /products fetch
                    if (!product) { this.classList.remove('loading-gallery'); return; }

                    this.render(product);
                    this.classList.remove('loading-gallery');
                    if (window.isTouch) {
                        this.addLink(product);
                    }
                });
            } else {
                console.error('Product loading function is not available.');
            }

        }
    }

    /**
     * Render component.
     * 
     * @param {object} product Product data.
     * @returns {undefined}
     */
    render(product) {

        if (product.images.length > 1) {

            this.injectImages(product);

            this.initSlider();
        }
    }

    renderFromMedia(media) {
        if (!this.dataset.inject) {
            let div = document.createElement('div');
    
            if (this.offsetHeight == 0) {
                div.style.height = this.parentElement.offsetHeight + 'px';
            } else {
                div.style.height = this.offsetHeight + 'px';
            }
    
            div.classList.add('product-gallery__images');
            div.classList.add('swiper');
    
            let html = '';
            html = html + '<div class="swiper-wrapper">';
    
            let imageCount = 0;
            const maxImages = 4; 
    
            media.forEach(item => {
                if (imageCount >= maxImages) return;
        
                if (item.media_type == 'video') {
                    html = html + `<div class="swiper-slide" style="display: flex; justify-content: center; align-items: center; width: 100%; height: 100%; position: relative;">
                        <a class="product-card__link product-card__slide-link" href='/products/${this.dataset.handle}' alt='page Link'></a>
                        <video class="product-gallery__video" style="width: 100%; height: 100%; object-fit: cover;" autoplay loop muted playsinline>
                            ${item.sources.map(source => `<source src="${source.url}" type="${source.mime_type}">`).join('')}
                            Your browser does not support the video tag.
                        </video>
                    </div>`;
    
                    imageCount++;

                } else {
                    html = html + `<div style="width: 100%; display: block; position: relative;" class="swiper-slide product-gallery__images-item">
                        <a class="product-card__link product-card__slide-link" href='/products/${this.dataset.handle}' alt='page Link'></a>
                        <img loading="lazy" src='${item.src}' alt='Product image'/>
                    </div>`;
    
                    imageCount++;
                }
            });
    
            html = html + '</div>';
    
            if (media.length > 3) {
                html = html + '<div class="swiper-pagination large"></div><div class="swiper-button-prev transparent-button"></div><div class="swiper-button-next transparent-button"></div>';
            } else {
                html = html + '<div class="swiper-pagination"></div><div class="swiper-button-prev transparent-button"></div><div class="swiper-button-next transparent-button"></div>';
            }
    
            div.innerHTML = html;
            this.append(div);
            this.dataset.inject = true;
        }

        this.initSlider();
        this.classList.add('slide-active');
        let element = this.querySelector('.product-gallery__images');
        if (element) {
            element.classList.add('active');
        }
    }

    /**
     * Inject images.
     * 
     * @param {object} product Product data.
     * @returns {undefined}
     */
    injectImages(product) {
        if (!this.dataset.inject) {
            let div = document.createElement('div');
    
            if (this.offsetHeight == 0) {
                div.style.height = this.parentElement.offsetHeight + 'px';
            } else {
                div.style.height = this.offsetHeight + 'px';
            }
    
            div.classList.add('product-gallery__images');
            div.classList.add('hidden');
            div.classList.add('swiper');
    
            let html = '';
            html = html + '<div class="swiper-wrapper">';
    
            let imageCount = 0;
            const maxImages = 4; 
    
            product.images.forEach(image => {
                if (imageCount >= maxImages) return;
        
                if (image.media_type == 'video') {
                    html = html + `<div class="swiper-slide" style="display: flex; justify-content: center; align-items: center; width: 100%; height: 100%; position: relative;">
                        <a class="product-card__link product-card__slide-link" href='/products/${product.handle}' alt='${product.title} page Link'></a>
                        <video class="product-gallery__video" style="width: 100%; height: 100%; object-fit: cover;" autoplay loop muted playsinline>
                            ${image.sources.map(source => `<source src="${source.url}" type="${source.mime_type}">`).join('')}
                            Your browser does not support the video tag.
                        </video>
                    </div>`;
    
                    imageCount++;

                } else {
                    let src = image.src;
    
                    if (this.dataset.size) {
                        let ext = window.theme.file.getExtension(src);
                        let lastIndex = src.lastIndexOf(ext);
                        
                        if (lastIndex !== -1) {
                            image.src = src.substring(0, lastIndex) + '_' + this.dataset.size + '_crop_top' + ext;
                        }
                    }
    
                    html = html + `<div style="width: 100%; display: block; position: relative;" class="swiper-slide product-gallery__images-item">
                        <a class="product-card__link product-card__slide-link" href='/products/${product.handle}' alt='${product.title} page Link'></a>
                        <img loading="lazy" src='${image.src}' alt='${product.title}'/>
                    </div>`;
    
                    imageCount++;
                }
            });
    
            html = html + '</div>';
    
            if (product.images.length > 3) {
                html = html + '<div class="swiper-pagination large"></div><div class="swiper-button-prev transparent-button"></div><div class="swiper-button-next transparent-button"></div>';
            } else {
                html = html + '<div class="swiper-pagination"></div><div class="swiper-button-prev transparent-button"></div><div class="swiper-button-next transparent-button"></div>';
            }
    
            div.innerHTML = html;
            this.append(div);
            this.dataset.inject = true;
        }
    }

    /**
     * Init slider.
     * 
     * @returns {undefined}
     */
    initSlider() {

        this.slider = new Swiper( this.querySelector('.product-gallery__images'), {
            preloadImages: false,
            lazy: {
                loadPrevNext: true,
            },
            watchSlidesProgress: true,
            autoHeight: true,
            pagination: {
                el: '.swiper-pagination',
                dynamicMainBullets: 3,
                dynamicBullets: true
            },
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
            on: {
                init: (slide) => {
                    
                    let element = this.querySelector('.product-gallery__images');

                    element.classList.remove('hidden');

                    element.classList.add('active');

                    if (window.isTouch) {
                        
                        this.classList.add('slide-active');

                    } else {

                        setTimeout(() => {

                            if (this.classList.contains('hover')) {
                                this.classList.add('slide-active');
                            }

                        }, 400);
                    }
                },
                slideChange: index => {}
            }
        });
    }

    /**
     * Destroy compoent.
     * 
     * @returns {undefined}
     */
    destroy() {

        if (this.dataset.preRendered === 'true' || this.dataset.media) {
            return;
        }

        this.classList.remove('active');

        if (this.querySelector('.product-gallery__images')) {

            let element = this.querySelector('.product-gallery__images');

            element.classList.remove('active');

            this.classList.remove('slide-active');

            // We need to use set time out to avoid issues with accordion animation
            setTimeout(() => {
                element.classList.add('hidden');
        
                if (this.slider) {
                    this.slider.destroy();
                    this.classList.remove('slide-active');
                }
            }, 400);
        }
    }

     /**
     * add Link to PDP
     * 
     * @param {object} product Product data.
     * @returns {undefined}
     */
      addLink(product) {

        this.querySelectorAll("img").forEach(image => {

            image.addEventListener('click', () => {
                
                window.location.href = window.location.protocol + "//" + window.location.host + "/products/" + product.handle;
            
            });

        });

    }
}

customElements.define('product-gallery-component', productGalleryComponent);
